import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';
import type { Card } from '@/hooks/useCards';

export type CommunityPost = Tables<'community_posts'> & {
  profile_username?: string;
  profile_emoji?: string;
  like_count?: number;
  user_liked?: boolean;
};

export type TradeListing = Tables<'trade_listings'> & {
  card?: Card;
  profile_username?: string;
};

export type AppEvent = Tables<'events'> & {
  rsvp_count?: number;
  user_rsvped?: boolean;
};

export function useCommunityPosts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['community-posts'],
    queryFn: async () => {
      const { data: posts, error } = await supabase
        .from('community_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      if (!posts?.length) return [];

      const userIds = [...new Set(posts.map(p => p.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('user_id, username, avatar_emoji').in('user_id', userIds);
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));

      const postIds = posts.map(p => p.id);
      const { data: likes } = await supabase.from('community_post_likes').select('post_id, user_id').in('post_id', postIds);
      const likeCountMap = new Map<string, number>();
      const userLikeSet = new Set<string>();
      (likes || []).forEach(l => {
        likeCountMap.set(l.post_id, (likeCountMap.get(l.post_id) || 0) + 1);
        if (user && l.user_id === user.id) userLikeSet.add(l.post_id);
      });

      return posts.map(p => ({
        ...p,
        profile_username: profileMap.get(p.user_id)?.username || 'Unknown',
        profile_emoji: profileMap.get(p.user_id)?.avatar_emoji || '🧙',
        like_count: likeCountMap.get(p.id) || 0,
        user_liked: userLikeSet.has(p.id),
      })) as CommunityPost[];
    },
  });
}

export function useCreateCommunityPost() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ text, tag, imageUrl }: { text: string; tag: string; imageUrl?: string | null }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('community_posts').insert({
        user_id: user.id,
        text_content: text,
        tag: tag as any,
        image_url: imageUrl || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['community-posts'] }),
  });
}

export function useToggleCommunityLike() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, liked }: { postId: string; liked: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      if (liked) {
        await supabase.from('community_post_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('community_post_likes').insert({ post_id: postId, user_id: user.id });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['community-posts'] }),
  });
}

export function useTradeListings() {
  return useQuery({
    queryKey: ['trade-listings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trade_listings')
        .select('*, card:cards(*)')
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      if (!data?.length) return [];

      const userIds = [...new Set(data.map(d => d.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('user_id, username').in('user_id', userIds);
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));

      return data.map(d => ({
        ...d,
        profile_username: profileMap.get(d.user_id)?.username || 'Unknown',
      })) as TradeListing[];
    },
  });
}

export function useCreateTradeListing() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ haveCardId, wantDescription, location }: { haveCardId: string; wantDescription: string; location?: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('trade_listings').insert({
        user_id: user.id,
        have_card_id: haveCardId,
        want_description: wantDescription,
        location_text: location || null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trade-listings'] }),
  });
}

export function useEvents() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const { data: events, error } = await supabase
        .from('events')
        .select('*')
        .gte('event_date', new Date().toISOString())
        .order('event_date', { ascending: true })
        .limit(20);
      if (error) throw error;
      if (!events?.length) return [];

      const eventIds = events.map(e => e.id);
      const { data: rsvps } = await supabase.from('event_rsvps').select('event_id, user_id').in('event_id', eventIds);
      
      const rsvpCountMap = new Map<string, number>();
      const userRsvpSet = new Set<string>();
      (rsvps || []).forEach(r => {
        rsvpCountMap.set(r.event_id, (rsvpCountMap.get(r.event_id) || 0) + 1);
        if (user && r.user_id === user.id) userRsvpSet.add(r.event_id);
      });

      return events.map(e => ({
        ...e,
        rsvp_count: rsvpCountMap.get(e.id) || 0,
        user_rsvped: userRsvpSet.has(e.id),
      })) as AppEvent[];
    },
  });
}

export function useCreateEvent() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (event: { name: string; event_type: string; store_name: string; event_date: string; location_text?: string; total_spots?: number }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('events').insert({
        created_by: user.id,
        name: event.name,
        event_type: event.event_type as any,
        store_name: event.store_name,
        event_date: event.event_date,
        location_text: event.location_text || null,
        total_spots: event.total_spots || 20,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useToggleRsvp() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, rsvped }: { eventId: string; rsvped: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      if (rsvped) {
        await supabase.from('event_rsvps').delete().eq('event_id', eventId).eq('user_id', user.id);
      } else {
        await supabase.from('event_rsvps').insert({ event_id: eventId, user_id: user.id });
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}
